import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  TextField,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import RuleIcon from "@mui/icons-material/Rule";
import { JsonView } from "react-json-view-lite";
import { jsonViewerStyles } from "../utils/jsonViewerStyles";
import { playJSONDownloadSound } from "../utils/soundUtils";
import { generateRulesJson } from "./FormRulesPreview";
import { parseFormJSON } from "../utils/formParser";
import { generateFormJson } from "../utils/formJsonBuilder";

const Preview = ({
  fields,
  sections,
  signatures = {},
  selectedCreators = [],
  formDetails = {},
  lineNumbers = false,
  formUploadSections = [],
  onFormJsonEdit,
  setSnackbar,
  signatureAnnotations = [],
  initialsAnnotations = [],
  additionalFields = [],
  signerAttributeBindings = [],
  initialerAttributeBindings = [],
  subsequentSigning = {},
  subsequentSigningAttributeBindings = [],
  formRules = { formNotes: "", fields: [] },
  offerCompare = { fields: [] },
  isPurchaseContract = false,
  extraFormSummary = {},
  extraDealSummary = {},
  rulesJSON = null,
  initials = null,
}) => {
  const [activeTab, setActiveTab] = useState("form");
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedJson, setEditedJson] = useState("");
  const [jsonError, setJsonError] = useState(null);

  const generateJson = () => generateFormJson({ fields, sections, signatures, selectedCreators, formDetails, lineNumbers, formUploadSections, signatureAnnotations, initialsAnnotations, additionalFields, signerAttributeBindings, initialerAttributeBindings, subsequentSigning, subsequentSigningAttributeBindings, isPurchaseContract, extraFormSummary, extraDealSummary, rulesJSON, initials });

  const getOfferCompareJson = () =>
    JSON.stringify(offerCompare.fields, null, 2);

  const getFormRulesJson = () =>
    generateRulesJson(formRules, formDetails.formId || "00000000-0000-0000-0000-000000000000");

  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);

  const getFormTypeFilename = () => {
    return formDetails.type
      ? formDetails.type.toLowerCase().replace(/\s+/g, "-")
      : "form";
  };

  const downloadJsonFile = async (json, suggestedName) => {
    const blob = new Blob([json], { type: "application/json" });

    try {
      if ("showSaveFilePicker" in window) {
        const handle = await window.showSaveFilePicker({
          suggestedName,
          types: [
            {
              description: "JSON File",
              accept: { "application/json": [".json"] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
      } else {
        throw new Error("File System Access API not supported");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = suggestedName;
        a.style.position = "fixed";
        a.style.left = "-9999px";
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 100);
      } else {
        return; // User cancelled — skip sound
      }
    }

    playJSONDownloadSound();
  };

  const handleDownloadFormJson = () => {
    setDownloadMenuAnchor(null);
    downloadJsonFile(generateJson(), `${getFormTypeFilename()}.json`);
  };

  const handleDownloadOfferCompare = () => {
    setDownloadMenuAnchor(null);
    downloadJsonFile(getOfferCompareJson(), `${getFormTypeFilename()}-offer-compare.json`);
  };

  const handleDownloadFormRules = () => {
    setDownloadMenuAnchor(null);
    downloadJsonFile(getFormRulesJson(), "form-rules.json");
  };

  const handleEnterEditMode = () => {
    const json =
      activeTab === "form"
        ? generateJson()
        : activeTab === "offerCompare"
        ? getOfferCompareJson()
        : getFormRulesJson();
    setEditedJson(json);
    setJsonError(null);
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedJson("");
    setJsonError(null);
  };

  const handleApplyChanges = () => {
    try {
      if (activeTab === "form") {
        const result = parseFormJSON(editedJson);
        if (!result.success) {
          throw new Error(result.errors.join("\n"));
        }
        if (onFormJsonEdit) onFormJsonEdit(result.data);
        setIsEditMode(false);
        setJsonError(null);
        if (setSnackbar) setSnackbar({ open: true, message: "Changes applied successfully.", severity: "success" });
      } else if (activeTab === "offerCompare") {
        const parsedJson = JSON.parse(editedJson);
        if (!Array.isArray(parsedJson)) {
          throw new Error("Offer Compare JSON must be an array");
        }
        setIsEditMode(false);
        setJsonError(null);
        if (setSnackbar) setSnackbar({ open: true, message: "JSON is valid. (Offer Compare changes are view-only.)", severity: "info" });
      } else {
        const parsedJson = JSON.parse(editedJson);
        if (!parsedJson || Array.isArray(parsedJson) || typeof parsedJson !== "object") {
          throw new Error("Form Rules JSON must be an object");
        }
        setIsEditMode(false);
        setJsonError(null);
        if (setSnackbar) setSnackbar({ open: true, message: "JSON is valid. (Form Rules changes are view-only.)", severity: "info" });
      }
    } catch (error) {
      setJsonError(error.message);
    }
  };


  return (
    <Card>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            p: 2,
          }}
        >
          <Typography variant="h6">JSON Preview</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant={activeTab === "form" ? "contained" : "outlined"}
              onClick={() => setActiveTab("form")}
              size="small"
            >
              Form JSON
            </Button>
            <Button
              variant={
                activeTab === "offerCompare" ? "contained" : "outlined"
              }
              onClick={() => setActiveTab("offerCompare")}
              size="small"
              disabled={!isPurchaseContract}
            >
              Offer Compare
            </Button>
            <Button
              variant={
                activeTab === "formRules" ? "contained" : "outlined"
              }
              onClick={() => setActiveTab("formRules")}
              size="small"
            >
              Form Rules
            </Button>
          </Box>
          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
            {!isEditMode ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEnterEditMode}
                  size="small"
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                  size="small"
                >
                  Download
                </Button>
                <Menu
                  anchorEl={downloadMenuAnchor}
                  open={Boolean(downloadMenuAnchor)}
                  onClose={() => setDownloadMenuAnchor(null)}
                >
                  <MenuItem onClick={handleDownloadFormJson}>
                    <ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Form JSON</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleDownloadOfferCompare} disabled={!isPurchaseContract}>
                    <ListItemIcon><CompareArrowsIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Offer Compare</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleDownloadFormRules}>
                    <ListItemIcon><RuleIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Form Rules</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEdit}
                  size="small"
                  color="error"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleApplyChanges}
                  size="small"
                  color="success"
                >
                  Apply Changes
                </Button>
              </>
            )}
          </Box>
        </Box>
        <CardContent>
          {jsonError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJsonError(null)}>
              <strong>JSON Error:</strong> {jsonError}
            </Alert>
          )}

          {isEditMode ? (
            <TextField
              multiline
              fullWidth
              value={editedJson}
              onChange={(e) => setEditedJson(e.target.value)}
              variant="outlined"
              minRows={20}
              maxRows={40}
              sx={{
                fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
                fontSize: "14px",
                "& .MuiInputBase-root": {
                  fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
                  fontSize: "14px",
                  backgroundColor: "#1e1e1e",
                  color: "#d4d4d4",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#3c3c3c",
                },
              }}
            />
          ) : (
            <Box
              sx={{
                bgcolor: "#1e1e1e",
                borderRadius: 1,
                p: 2,
                overflow: "auto",
                "& *": { fontWeight: "normal !important" },
              }}
            >
              <JsonView
                data={JSON.parse(
                  activeTab === "form"
                    ? generateJson()
                    : activeTab === "offerCompare"
                    ? getOfferCompareJson()
                    : getFormRulesJson()
                )}
                style={jsonViewerStyles}
              />
            </Box>
          )}
        </CardContent>
      </Card>
  );
};

export default Preview;
